import type { ReactNode } from 'react';
import { classMerge } from '@/chassis/core/common-helpers/class-merge';
import DocumentActionButtons from '../action/DocumentActionButtons';
import Lucide from '../icons/Lucide';

type DocumentCardProps = {
    title: ReactNode;
    subtitle?: ReactNode;
    metadata?: ReactNode;
    url?: string;
    fileName?: string;
    onOpen?: () => void | Promise<void>;
    onDownload?: () => void | Promise<void>;
    onRemove?: () => void | Promise<void>;
    icon?: string;
    actions?: ReactNode;
    className?: string;
    contentClassName?: string;
    iconContainerClassName?: string;
    iconClassName?: string;
    titleClassName?: string;
    subtitleClassName?: string;
    metadataClassName?: string;
    actionsClassName?: string;
    actionSize?: 'sm' | 'lg';
    openLabel?: string;
    downloadLabel?: string;
    removeLabel?: string;
};

const resolveFileExtensionBadge = (value?: string | null): string | undefined => {
    const normalized = (value ?? '').trim().split('?')[0].split('#')[0];
    const lastDotIndex = normalized.lastIndexOf('.');
    if (lastDotIndex <= 0 || lastDotIndex === normalized.length - 1) {
        return undefined;
    }

    const extension = normalized.slice(lastDotIndex + 1).toUpperCase();
    if (!/^[A-Z0-9]{2,8}$/.test(extension)) {
        return undefined;
    }

    return extension;
};

export default function DocumentCard({
    title,
    subtitle,
    metadata,
    url,
    fileName,
    onOpen,
    onDownload,
    onRemove,
    icon = 'FileText',
    actions,
    className,
    contentClassName,
    iconContainerClassName,
    iconClassName,
    titleClassName,
    subtitleClassName,
    metadataClassName,
    actionsClassName,
    actionSize = 'sm',
    openLabel,
    downloadLabel,
    removeLabel,
}: DocumentCardProps) {
    const extensionBadge = resolveFileExtensionBadge(fileName) ?? (typeof title === 'string' ? resolveFileExtensionBadge(title) : undefined);

    return (
        <div className={classMerge('flex flex-col gap-4 rounded-lg border border-slate-200 p-4 lg:flex-row lg:items-center', className)}>
            <div className="flex flex-1 items-center gap-4">
                <div className={classMerge('flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary', iconContainerClassName)}>
                    <Lucide icon={icon as any} className={classMerge('h-6 w-6', iconClassName)} />
                </div>
                <div className={classMerge('min-w-0', contentClassName)}>
                    <h4 className={classMerge('text-sm font-semibold text-slate-900', titleClassName)}>{title}</h4>
                    {(subtitle || extensionBadge) ? (
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                            {subtitle ? <p className={classMerge('text-xs text-slate-500', subtitleClassName)}>{subtitle}</p> : null}
                            {extensionBadge ? (
                                <span className="inline-flex w-fit rounded-none border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-slate-600">
                                    {extensionBadge}
                                </span>
                            ) : null}
                        </div>
                    ) : null}
                    {metadata ? <p className={classMerge('text-xs text-slate-500', metadataClassName)}>{metadata}</p> : null}
                </div>
            </div>
            <div className={actionsClassName || 'flex items-center gap-2'}>
                <DocumentActionButtons
                    url={url}
                    fileName={fileName}
                    onOpen={onOpen}
                    onDownload={onDownload}
                    onRemove={onRemove}
                    openLabel={openLabel}
                    downloadLabel={downloadLabel}
                    removeLabel={removeLabel}
                    size={actionSize}
                />
                {actions}
            </div>
        </div>
    );
}